import { useClientPortalSubmit } from "@certn/api";
import {
  Container,
  SectionMessage,
  Stack,
  Text,
  TextInputFormikField,
  ProgressLoader,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useCallback, useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { object, string } from "yup";

import {
  LeverIntegrationContext,
  AtsIntegration,
} from "../../LeverIntegrationContext";
import { LeverIntegrationStepLayout } from "../LeverIntegrationStepLayout";
import { ApiKeyWhatsThis } from "../ApiKeyWhatsThis";

export function ApiKeyStep() {
  const { t } = useTranslation();
  const [apiKeyError, setApiKeyError] = useState(false);
  const {
    atsIntegrationData,
    setAtsIntegration,
    goToNextStep,
    setTriggerStages,
  } = useContext(LeverIntegrationContext);
  const validationSchema = object().shape({
    api_key: string().required().label(t("API key")),
  });

  const { submit } = useClientPortalSubmit(
    "/api/internal/client-portal/ats-integrations/trigger-stages/",
    {
      onFailure: () => {
        setApiKeyError(true);
      },
      onSuccess: (triggerStages) => {
        setTriggerStages(triggerStages);
        goToNextStep();
      },
    }
  );

  const handleSubmit = useCallback(
    async (values: AtsIntegration) => {
      setApiKeyError(false);
      setAtsIntegration(values);
      await submit(values);
    },
    [setAtsIntegration, submit]
  );

  return (
    <Formik
      initialValues={atsIntegrationData}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, values }) => {
        return (
          <LeverIntegrationStepLayout
            onComplete={handleSubmit}
            disableCompletion={!isValid}
            submitting={isSubmitting}
          >
            <Container
              layout="narrow"
              testID="lever-details-page-outermost-container"
            >
              <Stack gap="space-2">
                <div>
                  <Text style="input/section-title">
                    {t("step {{stepNumber}} of {{stepCount}}", {
                      stepNumber: 1,
                      stepCount: 2,
                    })}
                  </Text>
                </div>
                <Text
                  as="h1"
                  className={styles.titleSpacing}
                  style="global/title-small"
                >
                  {t("{{integration}} integration", { integration: "Lever" })}
                </Text>
                <ProgressLoader floating value={50} />
                <div className={styles.mainContentContainer}>
                  {apiKeyError && (
                    <SectionMessage
                      type="danger"
                      sectionTitle={t("API verification failed")}
                      icon="error"
                    >
                      <Trans>
                        Please check your API key and make sure it has been
                        entered correctly.
                      </Trans>
                    </SectionMessage>
                  )}
                  <section>
                    <TextInputFormikField
                      name="api_key"
                      label={t("API key")}
                      testID="api-key-input"
                      info=<ApiKeyWhatsThis />
                      isRequired
                      isDisabled={isSubmitting}
                    />
                  </section>
                </div>
              </Stack>
            </Container>
          </LeverIntegrationStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
};
